import styles from './ClpEligibilityTable.module.scss';
import { useEffect, useMemo, useState } from 'react';
import TableWithPagination from '../TableWithPagination';
import { TableCellText } from "@mds/web-ui-table";
import { Text } from "@mds/web-ui-text";
import { Link } from "@mds/web-ui-link";
import { IconDocument, IconDownload } from "@mds/web-ui-icons";
import { fetchClpEligibilityData } from "../../Service/getMetadataResult";
import { MdsIcon, MdsTooltip } from '@mds/react';
import { downloadFileByCorrId } from '../../Service/FileDownloadService';
import { getTableDataFromApiResponse, getTableDataFromApiResponseRecords } from '../../util/dataUtils';
import type { ApiResponse, ApiResponseRecord } from '../../models/ApiResponse';
import type { TableData } from '../../models/TableData';
import { FileUploadStatus, type ClpError } from '../../types/clpTypes';

const DownloadCell = ({ onError, value  }: {onError: (errors: ClpError[]) => void, value: { correlationId?: string | null, uploadFileName?: string | null }}) => (
  <TableCellText
    text={
      <Link
        href="#"
        icon={<IconDownload aria-hidden />}
        iconPosition="leading"
        onClick={() => handleDownloadFile(onError,value.correlationId)}
        textStyle="bodyMediumHeavier"
      >
        Download File
      </Link>
    }
  />
);

/**
 * Renders a name cell to show the name of the file.
 *
 * @param {string | undefined} props.value - The name of the file.
 * @return {JSX.Element} The rendered name cell.
 */
const NameCell = ({ value }: { value: string | undefined }) => (
  <TableCellText
    text={<span>
      <IconDocument data-testid="file-name-icon" size={"small"} color={"info"} aria-hidden style={{ marginRight: '5px' }} />
      {value}
    </span>}
  />
);

/**
 * Renders a status cell to show whether the file was successful, partially successful or failed.
 *
 * @param {number | undefined} props.value - The value to determine the status.
 * @return {JSX.Element} The rendered status cell.
 */
const StatusCell = ({ value }: { value: number | undefined }) => {
  let iconName = '';
  let iconColor: "inverse" | "interactive" | "warning" | "success" | "error" | "default" = "default";
  let message = '';

  if (value === FileUploadStatus.SUCCESS.valueOf()) {
    iconName = 'ico_checkmark_filled';
    iconColor = 'success';
    message = "You successfully checked your addresses. Download and see results in Eligibility column."
  } else if (value === FileUploadStatus.PARTIAL_SUCCESS.valueOf()) {
    iconName = 'ico_alert_triangle';
    iconColor = 'warning';
    message = "You successfully checked your addresses, but some can't be checked. Download, see Error column, correct and try again."
  } else if (value === FileUploadStatus.FAILED.valueOf()) {
    iconName = 'ico_alert_filled';
    iconColor = 'error';
    message = "We can't check those addresses. Download file, see Error column, correct and try again."
  } else {
    iconName = 'ico_ellipsis_circle';
    iconColor = 'error';
    message = "We can't check the status of this file. Download to see results in Eligibility column.";
  }

  return (
    <TableCellText
      text={<span>
        <MdsIcon
          type={iconName}
          color={iconColor}
          size={20}>
            <MdsTooltip message={message}/>
        </MdsIcon>
      </span>}
    />
  );
}

interface ClpEligibilityTableProps {
  records: ApiResponseRecord[] | null;
  onError: (error: ClpError[]) => void;
}

function handleDownloadFile(onError: (errors: ClpError[]) => void, correlationId?: string | null) {
  onError([]);

  downloadFileByCorrId(correlationId)
    .catch((err: Error) => {
      console.log('Error in handleDownloadFile: ', err);
      const error: ClpError = {type: 'system', message: err.message, subtype: 'download'};
      onError([error]);
    });
}

const ClpEligibilityTable = (props: ClpEligibilityTableProps) =>{
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (props.records !== null) {
      setData(getTableDataFromApiResponseRecords(props.records));
      setLoading(false);
    }
  }, [props.records]);

  useEffect(() => {
    if (props.records === null)
      fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data: ApiResponse = await fetchClpEligibilityData();
      setData(getTableDataFromApiResponse(data));
    } catch (err: any) {
      console.log('Error in fetchData: ', err);
      const errMessage = (err instanceof Error) ? err.message : err.toString();
      const error: ClpError = {type: 'system', message: errMessage, subtype: 'history'};
      props.onError([error]);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: ' ',
        accessor: 'status' as const,
        Cell: StatusCell,
      },
      {
        Header: 'Name',
        accessor: 'uploadFileName' as const,
        Cell: NameCell,
      },
      {
        Header: 'Date',
        accessor: 'dateUploaded' as const,
      },
      {
        Header: 'Uploaded by',
        accessor: 'uploadedBy' as const,
      },
      {
        Header: 'Total addresses',
        accessor: 'totalAddresses' as const,
      },
      {
        Header: 'Eligible addresses',
        accessor: 'eligibleAddresses' as const,
      },
      {
        Header: 'Download',
        accessor: (row: TableData) => ({
          correlationId: row.correlationId,
          uploadFileName: row.uploadFileName,
        }),
        // TableWithPagination invokes this cell callback directly; it is not mounted as a React component type.
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ value }: { value: { correlationId?: string | null, uploadFileName?: string | null } }) => (
          <DownloadCell onError={props.onError} value={value} />
        ),
      },
    ],
    []
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (data.length === 0) {
    console.log("No data");
    return (
      <div className={styles['noData-container']} />
    );

  }

  console.log("We have data: " + data.length);

  return (
    <div className={styles.clpEligibilityTable}>
      <span className={styles['table-caption']} style={{ paddingTop: '10px', marginBottom: '2px' }}>Results</span>
      <div style={{ paddingBottom: '30px', paddingTop: '0px', color: '#565A5D', fontSize: '14px' }}>
        <Text>Download files to see address eligibility in the Download column.</Text>
      </div>
      <TableWithPagination<TableData> columns={columns} data={data} />
    </div>
  );
}

export default ClpEligibilityTable;
